import React from 'react';
import { useTemplateContactInfo, useTemplateInquiryForm } from './TemplateContactUtils.jsx';
import { Star } from 'lucide-react';
import { sanitizeCss, sanitizeHtml } from './Sanitizer.jsx';

const defaultTheme = {
  primary: '#0891b2',
  secondary: '#232f3e',
  accent: '#B88E2F',
  background: '#f8fafc',
  surface: '#ffffff',
  color: '#111827',
  mutedColor: '#6b7280',
  borderColor: '#e5e7eb',
  pageWidth: '1280px',
  buttonRadius: '999px',
  cardRadius: '14px',
  cardShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
  fontFamily: 'Inter, Arial, sans-serif'
};

const themeOf = (theme = {}) => ({ ...defaultTheme, ...theme });
const visibleItems = (items = []) => (Array.isArray(items) ? items.filter((item) => item?.visible !== false) : []);
const assetUrl = (asset, fallback = '') => {
  if (!asset) return fallback || '';
  if (typeof asset === 'string') return asset;
  return asset.displayLink || asset.storedLink || fallback || '';
};
const getImageUrl = (item = {}) => assetUrl(item.imageContent, item.imageUrl || item.imageURL);
const getBlocks = (section, key = 'blocks') => visibleItems(section?.content?.[key] || []);
const overlayColorWithOpacity = (color = '#111827', opacity = 0.5) => (
  String(color).startsWith('#')
    ? `${color}${Math.round(Number(opacity || 0) * 255).toString(16).padStart(2, '0')}`
    : color
);

const applyFutureSectionStyle = (base = {}, section = {}, theme = {}) => {
  const style = section?.style || {};
  const layout = section?.layout || {};
  const content = section?.content || {};
  const next = { ...base };
  const accent = style.glowColor || style.accentColor || theme.primary || '#0f766e';
  const layers = [];
  if (style.vignette) layers.push(`radial-gradient(ellipse at center, transparent 45%, ${style.vignetteColor || 'rgba(15,23,42,.34)'})`);
  if (style.gridOverlay) {
    const color = overlayColorWithOpacity(style.gridColor || '#94a3b8', 0.22);
    layers.push(`linear-gradient(${color} 1px, transparent 1px)`, `linear-gradient(90deg, ${color} 1px, transparent 1px)`);
    next.backgroundSize = `${style.gridSize || '32px'} ${style.gridSize || '32px'}`;
  }
  if (style.noiseOverlay) layers.push('repeating-linear-gradient(135deg, rgba(15,23,42,.035) 0 1px, transparent 1px 4px)');
  if (layers.length) next.backgroundImage = [...layers, next.backgroundImage].filter(Boolean).join(', ');
  if (style.surfaceMode === 'glass') {
    next.backdropFilter = `blur(${style.glassBlur || '18px'}) saturate(${style.backdropSaturate || '1.25'})`;
    next.WebkitBackdropFilter = next.backdropFilter;
    if (!style.backgroundImage && style.backgroundType !== 'image') next.backgroundColor = `rgba(255,255,255,${Number(style.glassOpacity || 0.74)})`;
  }
  if (style.surfaceMode === 'neo') next.boxShadow = next.boxShadow || '12px 12px 28px rgba(15,23,42,.12), -12px -12px 28px rgba(255,255,255,.75)';
  if (style.surfaceMode === 'brutal') {
    next.border = next.border || '2px solid currentColor';
    next.boxShadow = next.boxShadow || '8px 8px 0 rgba(15,23,42,.22)';
  }
  if (style.surfaceMode === 'luminous' || style.borderGlow) {
    next.boxShadow = [next.boxShadow, `0 0 ${style.glowSpread || '42px'} ${overlayColorWithOpacity(accent, style.glowStrength || 0.28)}`].filter(Boolean).join(', ');
  }
  if (style.backgroundBlendMode && style.backgroundBlendMode !== 'normal') next.backgroundBlendMode = style.backgroundBlendMode;
  if (style.sectionOpacity) next.opacity = Number(style.sectionOpacity);
  if (style.scrollSnap) next.scrollSnapAlign = 'start';
  if (style.stickyTop) {
    next.position = 'sticky';
    next.top = style.stickyTop;
    next.zIndex = 2;
  }
  if (style.animatedGradient) {
    next.backgroundSize = next.backgroundSize || '200% 200%';
    next.animation = [next.animation, 'templateGradientShift 14s ease infinite'].filter(Boolean).join(', ');
  }
  if (layout.contentDensity === 'dense' && !style.padding) next.padding = '48px 20px';
  if (layout.contentDensity === 'airy' && !style.padding) next.padding = '112px 24px';
  if (layout.edgeBleed) {
    next.width = '100vw';
    next.marginLeft = 'calc(50% - 50vw)';
    next.marginRight = 'calc(50% - 50vw)';
  }
  if (layout.safeArea) {
    next.paddingLeft = 'max(24px, env(safe-area-inset-left))';
    next.paddingRight = 'max(24px, env(safe-area-inset-right))';
  }
  if (content.readerMode === 'immersive') next.minHeight = next.minHeight || '80vh';
  if (content.readerMode === 'focused') {
    next.outline = next.outline || `1px solid ${overlayColorWithOpacity(style.accentColor || theme.primary || '#0f766e', 0.22)}`;
    next.outlineOffset = '-1px';
  }
  if (content.readerMode === 'compact' && !style.padding) next.padding = '40px 20px';
  return next;
};

const sectionStyle = (section, rawTheme) => {
  const theme = themeOf(rawTheme);
  const style = section?.style || {};
  const next = {
    backgroundColor: style.backgroundColor || theme.background,
    color: style.color || theme.color,
    padding: style.padding || '72px 24px',
    minHeight: style.minHeight,
    textAlign: style.textAlign || section?.layout?.align || 'left',
    overflow: 'hidden'
  };
  if (style.backgroundType === 'gradient' && style.backgroundGradient) {
    next.background = style.backgroundGradient;
  }
  if (style.backgroundImage && style.backgroundType !== 'none') {
    const overlay = overlayColorWithOpacity(style.overlayColor || '#111827', style.overlayOpacity || 0.5);
    next.backgroundImage = `${style.backgroundType === 'image' && style.overlayOpacity !== '0' ? `linear-gradient(${overlay}, ${overlay}), ` : ''}url(${style.backgroundImage})`;
    next.backgroundSize = style.backgroundSize || 'cover';
    next.backgroundPosition = style.backgroundPosition || 'center';
    next.backgroundRepeat = style.backgroundRepeat || 'no-repeat';
  }
  if (style.backgroundType === 'none') {
    delete next.background;
    delete next.backgroundColor;
    delete next.backgroundImage;
  }
  return applyFutureSectionStyle(next, section, theme);
};

const containerStyle = (section, rawTheme) => {
  const theme = themeOf(rawTheme);
  return {
    maxWidth: section?.layout?.width === 'full'
      ? 'none'
      : section?.layout?.width === 'narrow'
        ? section?.style?.maxWidth || '920px'
        : section?.style?.maxWidth || theme.pageWidth,
    margin: '0 auto'
  };
};

const RetailButton = ({ label, url, variant = 'primary', theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  const outline = variant === 'outline';
  return (
    <a href={url || '#'} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 42, padding: '0 18px', borderRadius: theme.buttonRadius, border: outline ? `1px solid ${theme.borderColor}` : '1px solid transparent', background: outline ? '#ffffff' : theme.primary, color: outline ? theme.color : '#ffffff', textDecoration: 'none', fontWeight: 800, fontSize: 14 }}>
      {label}
    </a>
  );
};

const ProductCard = ({ item, theme: rawTheme, buttonLabel }) => {
  const theme = themeOf(rawTheme);
  return (
    <div style={{ background: '#ffffff', borderRadius: theme.cardRadius, boxShadow: theme.cardShadow, overflow: 'hidden', border: `1px solid ${theme.borderColor}` }}>
      <div style={{ position: 'relative' }}>
        <img src={getImageUrl(item)} alt={item.title} style={{ width: '100%', height: 210, objectFit: 'cover' }} />
        {item.badge && <span style={{ position: 'absolute', top: 12, left: 12, background: theme.accent, color: '#fff', borderRadius: 999, padding: '5px 10px', fontSize: 12, fontWeight: 800 }}>{item.badge}</span>}
      </div>
      <div style={{ padding: 16 }}>
        <h3 style={{ margin: '0 0 6px', color: theme.primary, fontSize: 18 }}>{item.title}</h3>
        <p style={{ margin: '0 0 12px', color: theme.mutedColor, minHeight: 44 }}>{item.description}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div><strong style={{ fontSize: 20 }}>{item.price}</strong>{item.compareAt && <span style={{ color: theme.mutedColor, textDecoration: 'line-through', marginLeft: 8 }}>{item.compareAt}</span>}</div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#f59e0b', fontWeight: 800 }}><Star size={15} fill="currentColor" />{item.rating}</span>
        </div>
        <button type="button" style={{ marginTop: 14, width: '100%', border: 0, borderRadius: 999, background: theme.primary, color: '#fff', padding: '11px 14px', fontWeight: 800 }}>{item.buttonLabel || buttonLabel || 'Add to Cart'}</button>
      </div>
    </div>
  );
};

const getRetailGenericActions = (section) => {
  const content = section?.content || {};
  const actions = visibleItems(content.actions || []).filter((action) => action?.label);
  if (actions.length) return actions;

  return [
    content.primaryActionLabel ? { label: content.primaryActionLabel, url: content.primaryActionUrl, variant: 'primary' } : null,
    content.secondaryActionLabel ? { label: content.secondaryActionLabel, url: content.secondaryActionUrl, variant: 'outline' } : null
  ].filter(Boolean);
};

const getRetailGenericBlocks = (section) => {
  const content = section?.content || {};
  const keys = ['blocks', 'items', 'features', 'cards', 'products', 'categories', 'collections', 'benefits'];
  return keys.flatMap((key) => visibleItems(content[key] || []));
};

const RetailGenericSection = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  const content = section.content || {};
  const align = section.layout?.align || section.style?.textAlign || 'left';
  const title = content.title || content.heading || content.headline || content.name || '';
  const subtitle = content.subtitle || content.description || content.body || content.text || '';
  const imageUrl = assetUrl(content.heroImageContent, '') || assetUrl(content.mediaContent, '') || getImageUrl(content);
  const actions = getRetailGenericActions(section);
  const blocks = getRetailGenericBlocks(section);
  const html = sanitizeHtml(content.html || content.richText || content.bodyHtml || '');

  if (!title && !subtitle && !imageUrl && !actions.length && !blocks.length && !html && !section.customHtml) {
    return null;
  }

  return (
    <section style={sectionStyle(section, theme)}>
      <div className={imageUrl ? 'retail-render-two-col' : ''} style={{ ...containerStyle(section, theme), display: imageUrl ? 'grid' : 'block', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, .75fr)', gap: 34, alignItems: 'center', textAlign: align }}>
        <div>
          {content.eyebrow && <p style={{ margin: '0 0 10px', color: theme.primary, fontWeight: 900, letterSpacing: '.08em', textTransform: 'uppercase', fontSize: 12 }}>{content.eyebrow}</p>}
          {title && <h2 style={{ margin: '0 0 12px', fontSize: 'clamp(28px, 4vw, 44px)', lineHeight: 1.12 }}>{title}</h2>}
          {subtitle && <p style={{ margin: align === 'center' ? '0 auto 24px' : '0 0 24px', color: theme.mutedColor, maxWidth: 720, fontSize: 17, lineHeight: 1.7 }}>{subtitle}</p>}
          {html && <div dangerouslySetInnerHTML={{ __html: html }} />}
          {actions.length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: align === 'center' ? 'center' : 'flex-start', marginTop: 22 }}>
              {actions.map((action, index) => <RetailButton key={`${action.label}-${index}`} label={action.label} url={action.url || action.route} variant={action.variant} theme={theme} />)}
            </div>
          )}
        </div>
        {imageUrl && <img src={imageUrl} alt={content.imageAlt || title || 'Retail section'} style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: theme.cardRadius, boxShadow: theme.cardShadow }} />}
      </div>
      {blocks.length > 0 && (
        <div className="retail-render-product-grid" style={{ ...containerStyle(section, theme), marginTop: 32, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
          {blocks.map((item, index) => (
            <article key={`${item.title || item.label || item.name || index}-${index}`} style={{ border: `1px solid ${theme.borderColor}`, borderRadius: theme.cardRadius, background: theme.surface, boxShadow: theme.cardShadow, overflow: 'hidden' }}>
              {getImageUrl(item) && <img src={getImageUrl(item)} alt={item.imageAlt || item.title || 'Retail item'} style={{ width: '100%', height: 160, objectFit: 'cover' }} />}
              <div style={{ padding: 18 }}>
                {(item.price || item.value || item.icon) && <strong style={{ color: theme.primary }}>{item.price || item.value || item.icon}</strong>}
                {(item.title || item.label || item.name) && <h3 style={{ margin: '8px 0', fontSize: 18 }}>{item.title || item.label || item.name}</h3>}
                {(item.description || item.subtitle || item.body) && <p style={{ margin: 0, color: theme.mutedColor, lineHeight: 1.65 }}>{item.description || item.subtitle || item.body}</p>}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

const RetailHero = ({ section, theme }) => {
  const c = section.content || {};
  const image = getImageUrl(getBlocks(section, 'images')[0]) || getImageUrl(c) || '';
  const heroBackground = section.style?.backgroundType === 'image' && section.style?.backgroundImage
    ? {}
    : { backgroundImage: `linear-gradient(90deg, rgba(17,24,39,.74), rgba(17,24,39,.32)), url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  return (
    <section style={{ ...sectionStyle(section, theme), ...heroBackground, minHeight: section.style?.minHeight || '460px', padding: 0, display: 'flex', alignItems: 'center' }}>
      <div style={{ ...containerStyle(section, theme), width: '100%', padding: '86px 24px', color: '#ffffff' }}>
        <p style={{ margin: '0 0 16px', color: '#fde68a', fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>{c.eyebrow}</p>
        <h1 style={{ margin: '0 0 18px', maxWidth: 760, fontSize: 'clamp(42px, 6vw, 76px)', lineHeight: 1.02, fontWeight: 900 }}>{c.title}</h1>
        <p style={{ maxWidth: 660, fontSize: 18, lineHeight: 1.7, color: '#f8fafc' }}>{c.subtitle}</p>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 28 }}>
          <RetailButton label={c.primaryActionLabel} url={c.primaryActionUrl} theme={theme} />
          <RetailButton label={c.secondaryActionLabel} url={c.secondaryActionUrl} variant="outline" theme={theme} />
        </div>
      </div>
    </section>
  );
};

const RetailCategoryGrid = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  return (
    <section style={sectionStyle(section, theme)}>
      <div style={containerStyle(section, theme)}>
        <div style={{ textAlign: 'center', marginBottom: 34 }}>
          <h2 style={{ margin: 0, color: theme.color, fontSize: 'clamp(30px, 4vw, 44px)' }}>{section.content?.title}</h2>
          <p style={{ color: theme.mutedColor, margin: '10px auto 0', maxWidth: 620 }}>{section.content?.subtitle}</p>
        </div>
        <div className="retail-render-category-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 24 }}>
          {getBlocks(section).map((item) => (
            <a key={item.title} href={item.url || '#'} style={{ textAlign: 'center', color: theme.color, textDecoration: 'none' }}>
              <span style={{ display: 'block', width: 190, height: 190, margin: '0 auto 14px', borderRadius: section.content?.tileShape === 'square' ? theme.cardRadius : 999, overflow: 'hidden', boxShadow: theme.cardShadow }}>
                <img src={getImageUrl(item)} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </span>
              <strong style={{ fontSize: 18 }}>{item.title}</strong>
              <p style={{ color: theme.mutedColor, margin: '6px auto 0', maxWidth: 220 }}>{item.description}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const RetailFeatured = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  return (
    <section style={sectionStyle(section, theme)}>
      <div style={containerStyle(section, theme)}>
        <h2 style={{ textAlign: 'center', margin: '0 0 10px', fontSize: 'clamp(30px, 4vw, 44px)' }}>{section.content?.title}</h2>
        <p style={{ textAlign: 'center', color: theme.mutedColor, margin: '0 auto 32px', maxWidth: 620 }}>{section.content?.subtitle}</p>
        <div className="retail-render-product-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 22 }}>
          {getBlocks(section).map((item) => <ProductCard key={item.title} item={item} theme={theme} buttonLabel={section.content?.buttonLabel} />)}
        </div>
      </div>
    </section>
  );
};

const RetailProductGrid = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  return (
    <section style={sectionStyle(section, theme)}>
      <div style={containerStyle(section, theme)}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: 0, fontSize: 42 }}>{section.content?.title}</h1>
          <p style={{ color: theme.mutedColor, fontSize: 17 }}>{section.content?.subtitle}</p>
        </div>
        <div className="retail-render-catalog-grid" style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 26, alignItems: 'start' }}>
          <aside style={{ background: '#fff', borderRadius: theme.cardRadius, border: `1px solid ${theme.borderColor}`, boxShadow: theme.cardShadow, padding: 18 }}>
            <h3 style={{ margin: '0 0 16px' }}>{section.content?.filterTitle}</h3>
            <input placeholder={section.content?.searchPlaceholder} style={{ width: '100%', border: `1px solid ${theme.borderColor}`, borderRadius: 10, padding: '11px 12px', marginBottom: 16 }} />
            {visibleItems(section.content?.categories).map((item) => <label key={item.title} style={{ display: 'flex', gap: 8, marginBottom: 10 }}><input type="checkbox" readOnly />{item.title}</label>)}
          </aside>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, color: theme.mutedColor }}><span>{getBlocks(section).length} products</span><span>{section.content?.sortLabel}: Featured</span></div>
            <div className="retail-render-product-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 22 }}>
              {getBlocks(section).map((item) => <ProductCard key={item.title} item={item} theme={theme} buttonLabel={section.content?.buttonLabel} />)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const RetailProductDetail = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  const c = section.content || {};
  return (
    <section style={sectionStyle(section, theme)}>
      <div className="retail-render-two-col" style={{ ...containerStyle(section, theme), display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 46, alignItems: 'start' }}>
        <img src={getImageUrl(c)} alt={c.title} style={{ width: '100%', borderRadius: theme.cardRadius, boxShadow: theme.cardShadow, objectFit: 'cover', maxHeight: 560 }} />
        <div>
          {c.badge && <span style={{ display: 'inline-flex', borderRadius: 999, background: '#ecfeff', color: theme.primary, padding: '8px 12px', fontWeight: 900 }}>{c.badge}</span>}
          <h1 style={{ margin: '18px 0 10px', fontSize: 'clamp(36px, 5vw, 58px)', lineHeight: 1.05 }}>{c.title}</h1>
          <p style={{ color: theme.mutedColor, fontSize: 18, lineHeight: 1.7 }}>{c.subtitle}</p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', margin: '22px 0' }}><strong style={{ fontSize: 34 }}>{c.priceLabel}</strong><span style={{ color: theme.mutedColor, textDecoration: 'line-through' }}>{c.compareAtLabel}</span></div>
          <p style={{ color: '#f59e0b', fontWeight: 800 }}>{c.ratingLabel}</p>
          <label style={{ display: 'block', fontWeight: 800, margin: '20px 0 8px' }}>{c.quantityLabel}</label>
          <input type="number" defaultValue={1} min={1} style={{ width: 96, border: `1px solid ${theme.borderColor}`, borderRadius: 10, padding: '10px 12px' }} />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 24 }}>
            <RetailButton label={c.primaryActionLabel} theme={theme} />
            <RetailButton label={c.secondaryActionLabel} variant="outline" theme={theme} />
          </div>
          <div style={{ display: 'grid', gap: 12, marginTop: 28 }}>
            {getBlocks(section).map((item) => <div key={item.title} style={{ border: `1px solid ${theme.borderColor}`, borderRadius: 12, padding: 14 }}><strong>{item.title}</strong><p style={{ color: theme.mutedColor, margin: '4px 0 0' }}>{item.description}</p></div>)}
          </div>
        </div>
      </div>
    </section>
  );
};

const SummaryRow = ({ label, value, bold }) => <div style={{ display: 'flex', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #e5e7eb', fontWeight: bold ? 900 : 600 }}><span>{label}</span><span>{value}</span></div>;

const RetailCart = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  const subtotal = getBlocks(section).reduce((sum, item) => sum + Number(String(item.price || '0').replace(/[^0-9.]/g, '')), 0);
  const tax = subtotal * 0.08;
  return (
    <section style={sectionStyle(section, theme)}>
      <div className="retail-render-two-col" style={{ ...containerStyle(section, theme), display: 'grid', gridTemplateColumns: '1.4fr .8fr', gap: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 42 }}>{section.content?.title}</h1>
          <p style={{ color: theme.mutedColor }}>{section.content?.subtitle}</p>
          <div style={{ display: 'grid', gap: 14, marginTop: 24 }}>
            {getBlocks(section).map((item) => <div key={item.title} style={{ display: 'grid', gridTemplateColumns: '94px 1fr auto', gap: 16, alignItems: 'center', background: '#fff', border: `1px solid ${theme.borderColor}`, borderRadius: theme.cardRadius, padding: 14 }}><img src={getImageUrl(item)} alt={item.title} style={{ width: 94, height: 84, objectFit: 'cover', borderRadius: 10 }} /><div><strong>{item.title}</strong><p style={{ margin: '4px 0 0', color: theme.mutedColor }}>{item.description}</p></div><strong>{item.price}</strong></div>)}
          </div>
        </div>
        <aside style={{ background: '#fff', border: `1px solid ${theme.borderColor}`, borderRadius: theme.cardRadius, boxShadow: theme.cardShadow, padding: 24 }}>
          <h2 style={{ marginTop: 0 }}>{section.content?.summaryTitle}</h2>
          <SummaryRow label={section.content?.subtotalLabel} value={`$${subtotal.toFixed(2)}`} />
          <SummaryRow label={section.content?.taxLabel} value={`$${tax.toFixed(2)}`} />
          <SummaryRow label={section.content?.totalLabel} value={`$${(subtotal + tax).toFixed(2)}`} bold />
          <button type="button" style={{ width: '100%', border: 0, borderRadius: theme.buttonRadius, background: theme.primary, color: '#fff', padding: '13px 16px', fontWeight: 900, marginTop: 18 }}>{section.content?.checkoutLabel}</button>
        </aside>
      </div>
    </section>
  );
};

const RetailOrderSummary = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  return (
    <section style={sectionStyle(section, theme)}>
      <div style={containerStyle(section, theme)}>
        <h1 style={{ margin: 0, fontSize: 42 }}>{section.content?.title}</h1>
        <p style={{ color: theme.mutedColor, marginBottom: 26 }}>{section.content?.subtitle}</p>
        <div className="retail-render-product-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 20 }}>
          {getBlocks(section).map((item) => <div key={item.title} style={{ background: '#fff', border: `1px solid ${theme.borderColor}`, borderRadius: theme.cardRadius, padding: 20, boxShadow: theme.cardShadow }}><span style={{ color: theme.primary, fontWeight: 900 }}>{item.badge}</span><h3>{item.title}</h3><p style={{ color: theme.mutedColor }}>{item.description}</p><strong>{item.price}</strong></div>)}
        </div>
      </div>
    </section>
  );
};

const RetailAbout = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  const c = section.content || {};
  return (
    <section style={sectionStyle(section, theme)}>
      <div className="retail-render-two-col" style={{ ...containerStyle(section, theme), display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 52, alignItems: 'center' }}>
        <img src={getImageUrl(c)} alt={c.title} style={{ width: '100%', borderRadius: theme.cardRadius, boxShadow: theme.cardShadow }} />
        <div>
          <p style={{ color: theme.accent, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>{c.eyebrow}</p>
          <h2 style={{ fontSize: 'clamp(36px, 5vw, 58px)', lineHeight: 1.05, margin: '0 0 18px' }}>{c.title}</h2>
          <p style={{ color: theme.mutedColor, fontSize: 18, lineHeight: 1.75 }}>{c.subtitle}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, margin: '24px 0' }}>
            {getBlocks(section).map((item) => <div key={item.title} style={{ border: `1px solid ${theme.borderColor}`, borderRadius: 12, padding: 14 }}><strong style={{ fontSize: 24 }}>{item.value}</strong><p style={{ margin: '4px 0 0', color: theme.mutedColor }}>{item.title}</p></div>)}
          </div>
          <RetailButton label={c.primaryActionLabel} url={c.primaryActionUrl} theme={theme} />
        </div>
      </div>
    </section>
  );
};

const formField = (theme) => ({ width: '100%', border: `1px solid ${theme.borderColor}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontFamily: theme.fontFamily });

const RetailContact = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  const content = section.content || {};
  const contactInfo = useTemplateContactInfo(content);
  const c = { ...content, phoneValue: contactInfo.phoneValue, emailValue: contactInfo.emailValue, addressValue: contactInfo.addressValue };
  const fields = visibleItems(c.fields);
  const inquiryForm = useTemplateInquiryForm(fields);
  return (
    <section style={sectionStyle(section, theme)}>
      <div className="retail-render-two-col" style={{ ...containerStyle(section, theme), display: 'grid', gridTemplateColumns: '.9fr 1.1fr', gap: 52 }}>
        <div>
          <p style={{ color: theme.accent, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>{c.eyebrow}</p>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 58px)', margin: '0 0 18px' }}>{c.title}</h1>
          <p style={{ color: theme.mutedColor, fontSize: 18, lineHeight: 1.7 }}>{c.subtitle}</p>
          {[['phoneTitle', 'phoneValue'], ['emailTitle', 'emailValue'], ['addressTitle', 'addressValue']].map(([titleKey, valueKey]) => <div key={titleKey} style={{ marginTop: 24 }}><strong>{c[titleKey]}</strong><p style={{ color: theme.mutedColor, whiteSpace: 'pre-line' }}>{c[valueKey]}</p></div>)}
        </div>
        <form onSubmit={inquiryForm.submitInquiry} style={{ background: '#fff', border: `1px solid ${theme.borderColor}`, borderRadius: theme.cardRadius, boxShadow: theme.cardShadow, padding: 26 }}>
          <h2 style={{ marginTop: 0 }}>{c.formTitle}</h2>
          {fields.map((field) => {
            const fieldName = inquiryForm.fieldNameFromLabel(field.label, field.type);
            const style = { ...formField(theme), borderColor: inquiryForm.errors[fieldName] ? '#dc2626' : theme.borderColor };
            return field.type === 'textarea'
              ? <div key={field.label}><textarea placeholder={field.label} rows={5} value={inquiryForm.values[fieldName] || ''} onChange={(event) => inquiryForm.setFieldValue(fieldName, event.target.value)} style={style} />{inquiryForm.errors[fieldName] && <p style={{ margin: '-8px 0 12px', color: '#dc2626', fontSize: 12 }}>{inquiryForm.errors[fieldName]}</p>}</div>
              : <div key={field.label}><input placeholder={field.label} type={field.type || 'text'} value={inquiryForm.values[fieldName] || ''} onChange={(event) => inquiryForm.setFieldValue(fieldName, event.target.value)} style={style} />{inquiryForm.errors[fieldName] && <p style={{ margin: '-8px 0 12px', color: '#dc2626', fontSize: 12 }}>{inquiryForm.errors[fieldName]}</p>}</div>;
          })}
          {inquiryForm.status && <p style={{ color: inquiryForm.status.type === 'success' ? '#166534' : '#b91c1c', fontWeight: 700 }}>{inquiryForm.status.message}</p>}
          <button type="submit" disabled={inquiryForm.submitting} style={{ width: '100%', border: 0, borderRadius: theme.buttonRadius, background: theme.secondary, color: '#fff', padding: '13px 16px', fontWeight: 900, opacity: inquiryForm.submitting ? .72 : 1 }}>{inquiryForm.submitting ? 'Sending...' : c.submitLabel}</button>
        </form>
      </div>
    </section>
  );
};

const RetailPromo = ({ section, theme: rawTheme }) => {
  const theme = themeOf(rawTheme);
  const c = section.content || {};
  return (
    <section style={sectionStyle(section, theme)}>
      <div style={{ ...containerStyle(section, theme), textAlign: 'center' }}>
        <p style={{ margin: '0 0 12px', color: theme.accent, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>{c.eyebrow}</p>
        <h2 style={{ margin: '0 auto 14px', maxWidth: 780, fontSize: 'clamp(32px, 5vw, 54px)' }}>{c.title}</h2>
        <p style={{ maxWidth: 700, margin: '0 auto 24px', color: section.style?.color === '#ffffff' ? '#e5e7eb' : theme.mutedColor }}>{c.subtitle}</p>
        <RetailButton label={c.primaryActionLabel} url={c.primaryActionUrl} theme={theme} />
      </div>
    </section>
  );
};

const RetailCustomHtmlSection = ({ section, theme }) => {
  const scopeId = `retail-template-${section.sectionId || 'section'}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  const html = sanitizeHtml(section.customHtml || '');
  const css = sanitizeCss(section.customCss || '');
  return (
    <section id={scopeId} style={sectionStyle(section, theme)}>
      <style>{css}</style>
      {html ? <div dangerouslySetInnerHTML={{ __html: html }} /> : <div style={containerStyle(section, theme)}>Custom code was empty or removed by sanitizer.</div>}
    </section>
  );
};

export const retailResponsiveCss = `
  [data-template-id] .retail-render-product-grid,
  [data-template-id] .retail-render-category-grid,
  [data-template-id] .retail-render-catalog-grid,
  [data-template-id] .retail-render-two-col {
    min-width: 0;
  }
  @media (max-width: 980px) {
    [data-template-id] .retail-render-product-grid,
    [data-template-id] .retail-render-category-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    [data-template-id] .retail-render-catalog-grid,
    [data-template-id] .retail-render-two-col {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
  @media (max-width: 640px) {
    [data-template-id] .retail-render-product-grid,
    [data-template-id] .retail-render-category-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
`;

export const renderRetailSection = (section, theme) => {
  if (section.type === 'retailHeroCarousel') return <RetailHero section={section} theme={theme} />;
  if (section.type === 'retailCategoryGrid') return <RetailCategoryGrid section={section} theme={theme} />;
  if (section.type === 'retailFeaturedProducts') return <RetailFeatured section={section} theme={theme} />;
  if (section.type === 'retailProductGrid') return <RetailProductGrid section={section} theme={theme} />;
  if (section.type === 'retailProductDetail') return <RetailProductDetail section={section} theme={theme} />;
  if (section.type === 'retailCart') return <RetailCart section={section} theme={theme} />;
  if (section.type === 'retailOrderSummary') return <RetailOrderSummary section={section} theme={theme} />;
  if (section.type === 'retailAbout') return <RetailAbout section={section} theme={theme} />;
  if (section.type === 'retailContact') return <RetailContact section={section} theme={theme} />;
  if (section.type === 'retailPromoBanner') return <RetailPromo section={section} theme={theme} />;
  if (section.type === 'customHtml') return <RetailCustomHtmlSection section={section} theme={theme} />;
  return <RetailGenericSection section={section} theme={theme} />;
};
